import { useCallback, useEffect, useState } from "react";
import { crearEmpleado, actualizarEmpleado, darDeBajaEmpleado, listarPuestos } from "../../../../services/empleadosService";
import { normalizeEmpleado, buildEmpleadoPayload } from "../adapters/empleadosAdapter";
import { initialEmpleadoForm } from "../constants/rrhh";
import { showToast } from "../../../../utils/showToast";

const validar = (form) => {
  if (!form.nombres.trim())    return "El nombre es requerido.";
  if (!form.apellidos.trim())  return "El apellido es requerido.";
  if (!form.cedula.trim())     return "La cédula es requerida.";
  if (!form.tipoPersonal)      return "El tipo de personal es requerido.";
  if (!form.tipoContratacion)  return "El tipo de contratación es requerido.";
  return null;
};

export default function useEmpleadoForm({ onCreated, onUpdated, onDeleted }) {
  const [modal, setModal]               = useState("");
  const [modalImportar, setModalImportar] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [formNuevo, setFormNuevo]       = useState(initialEmpleadoForm);
  const [formEditar, setFormEditar]     = useState(initialEmpleadoForm);
  const [empleadoEditando, setEmpleadoEditando] = useState(null);
  const [puestos, setPuestos]           = useState([]);

  useEffect(() => {
    listarPuestos()
      .then((data) => setPuestos(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialEmpleadoForm);
    setModal("crear");
  }, []);

  const abrirEditar = useCallback((emp) => {
    if (!emp) return;
    const parsed = normalizeEmpleado(emp);
    setEmpleadoEditando(parsed);
    setFormEditar({
      nombres:          parsed.nombres,
      apellidos:        parsed.apellidos,
      cedula:           parsed.cedula,
      telefono:         parsed.telefono,
      email:            parsed.email,
      puestoId:         parsed.puestoId != null ? String(parsed.puestoId) : "",
      tipoPersonal:     parsed.tipoPersonal,
      tipoContratacion: parsed.tipoContratacion,
      fechaIngreso:     parsed.fechaIngreso ? String(parsed.fechaIngreso).substring(0, 10) : "",
      sueldoBase:       parsed.sueldoBase !== 0 ? String(parsed.sueldoBase) : "",
      ruc:              parsed.ruc || "",
      estado:           parsed.estado,
      observaciones:    parsed.observaciones,
      docenteId:        parsed.docenteId ?? null,
      usuarioId:        parsed.usuarioId ?? null,
    });
    setModal("editar");
  }, []);

  const abrirEliminar = useCallback((emp) => {
    if (!emp) return;
    setEmpleadoEditando(normalizeEmpleado(emp));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setEmpleadoEditando(null);
    setFormNuevo(initialEmpleadoForm);
    setFormEditar(initialEmpleadoForm);
  }, []);

  const handleCrear = useCallback(async () => {
    const err = validar(formNuevo);
    if (err) { showToast({ type: "warning", message: err }); return; }
    setSaving(true);
    try {
      const created = await crearEmpleado(buildEmpleadoPayload(formNuevo));
      onCreated?.(normalizeEmpleado(created));
      showToast({ type: "success", message: "Empleado registrado correctamente." });
      cerrarModal();
    } catch (e) {
      showToast({ type: "danger", message: e.message || "No se pudo registrar el empleado." });
    } finally { setSaving(false); }
  }, [formNuevo, onCreated, cerrarModal]);

  const handleActualizar = useCallback(async () => {
    if (!empleadoEditando) return;
    const err = validar(formEditar);
    if (err) { showToast({ type: "warning", message: err }); return; }
    setSaving(true);
    try {
      const updated = await actualizarEmpleado(empleadoEditando.id, buildEmpleadoPayload(formEditar));
      onUpdated?.(normalizeEmpleado({ ...empleadoEditando, ...updated }));
      showToast({ type: "success", message: "Empleado actualizado correctamente." });
      cerrarModal();
    } catch (e) {
      showToast({ type: "danger", message: e.message || "No se pudo actualizar el empleado." });
    } finally { setSaving(false); }
  }, [empleadoEditando, formEditar, onUpdated, cerrarModal]);

  const handleEliminar = useCallback(async () => {
    if (!empleadoEditando) return;
    setDeleting(true);
    try {
      await darDeBajaEmpleado(empleadoEditando.id);
      onDeleted?.(empleadoEditando);
      showToast({ type: "success", message: "Empleado dado de baja correctamente." });
      cerrarModal();
    } catch (e) {
      showToast({ type: "danger", message: e.message || "No se pudo dar de baja al empleado." });
    } finally { setDeleting(false); }
  }, [empleadoEditando, onDeleted, cerrarModal]);

  return {
    modal, puestos,
    formNuevo, setFormNuevo,
    formEditar, setFormEditar,
    empleadoEditando,
    saving, deleting,
    abrirCrear, abrirEditar, abrirEliminar, cerrarModal,
    handleCrear, handleActualizar, handleEliminar,
    modalImportar, setModalImportar,
  };
}
